import React from 'react';
import { Route, Link as RouterLink, Routes } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CloudQueue, GridOnRounded } from '@mui/icons-material';
import MuiDataGridPreview from './MuiDataGridPreview';
import FilterableCardsPreview from './FilterableCardsPreview';
import FilterableDataGridPreview from './FillterableDataGridPreview';
import PersistentServerSideDataGridPreview from './PersistentServerSideDataGridPreview';
import AuthSequencingPreview from './AuthSequencingPreview';
import DrawerComponentsPreview from './DrawerComponentsPreview';

const ComponentPreviewHome = () => {
  const components = [
    {
      color: 'primary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Preview various implementations of MUI DataGrids with different features and layouts.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <strong>Template:</strong> Standard layout with MainPanel
          </Typography>
        </>
      ),
      icon: <GridOnRounded fontSize="large" />,
      path: 'datagrid',
      title: 'MUI DataGrid',
    },
    {
      color: 'primary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Preview a new page layout with MUI components.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <strong>Template:</strong> FilterableDataGrid with PersistentDataGrid
          </Typography>
        </>
      ),
      icon: <GridOnRounded fontSize="large" />,
      path: 'filterable-datagrid',
      title: 'Filterable MUI DataGrid',
    },
    {
      color: 'secondary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Preview server-side DataGrid with pagination, sorting, and filtering for large datasets.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <strong>Template:</strong> PersistentServerSideDataGrid component
          </Typography>
        </>
      ),
      icon: <CloudQueue fontSize="large" />,
      path: 'server-side-datagrid',
      title: 'Server-Side DataGrid',
    },
    {
      color: 'secondary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Card component with customizable content and styling for various use cases.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <strong>Template:</strong> CustomizableCard with a grid layout
          </Typography>
        </>
      ),
      icon: <CloudQueue fontSize="large" />,
      path: 'customizable-card',
      title: 'Customizable Card',
    },
    {
      color: 'primary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Preview authorization sequencing with FilterableContent template and card grid layout.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <strong>Template:</strong>
            <ul>
              <li>FilterableContent</li>
              <li>GroupedCardContent</li>
              <li>AuthorizationSequencingCardContent</li>
            </ul>
          </Typography>
        </>
      ),
      icon: <GridOnRounded fontSize="large" />,
      path: 'auth-sequencing',
      title: 'Filterable Cards - Authorization Sequencing',
    },
    {
      color: 'primary',
      description: (
        <>
          <Typography color="text.secondary" variant="body2">
            Preview drawer components
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
            <ul>
              <li>Summary Screen</li>
            </ul>
          </Typography>
        </>
      ),
      icon: <GridOnRounded fontSize="large" />,
      path: 'drawer-components',
      title: 'Drawer Components',
    },
    // Future component previews can be added here
    // {
    //   title: 'Forms',
    //   description: 'Preview form components and input validation.',
    //   path: 'forms',
    //   icon: <FormIcon />,
    //   color: 'secondary',
    // },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Box sx={{ mb: 4 }}>
        <Typography gutterBottom variant="h3">
          Component Preview Dashboard
        </Typography>
        <Typography color="text.secondary" variant="body1">
          This dashboard provides preview pages for various UI components used throughout the
          application. Use these pages for design validation, layout testing, and data display
          verification.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        {components.map(component => (
          <Grid key={component.path} size={{ md: 4, sm: 6, xs: 12 }}>
            <Card
              sx={{
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-4px)',
                },
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: `${component.color}.50`,
                      borderRadius: 2,
                      color: `${component.color}.main`,
                      display: 'flex',
                      height: 64,
                      justifyContent: 'center',
                      width: 64,
                    }}
                  >
                    {component.icon}
                  </Box>
                  <Typography component="h2" variant="h5">
                    {component.title}
                  </Typography>
                  <Box>{component.description}</Box>
                </Stack>
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  color={component.color}
                  component={RouterLink}
                  fullWidth
                  to={component.path}
                  variant="contained"
                >
                  View Preview
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ bgcolor: 'grey.50', mt: 4 }}>
        <CardContent>
          <Stack spacing={1}>
            <Typography gutterBottom variant="h6">
              About Component Previews
            </Typography>
            <Typography color="text.secondary" variant="body2">
              • Use these previews to validate design consistency across the application
            </Typography>
            <Typography color="text.secondary" variant="body2">
              • Test component behavior with different data sets and configurations
            </Typography>
            <Typography color="text.secondary" variant="body2">
              • Verify responsive layouts and accessibility features
            </Typography>
            <Typography color="text.secondary" variant="body2">
              • Share preview links with designers and stakeholders for feedback
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

const Dashboard = () => {
  return (
    <Routes>
      <Route element={<ComponentPreviewHome />} index />
      <Route element={<MuiDataGridPreview />} path="datagrid" />
      <Route element={<FilterableDataGridPreview />} path="filterable-datagrid" />
      <Route element={<PersistentServerSideDataGridPreview />} path="server-side-datagrid" />
      <Route element={<FilterableCardsPreview />} path="customizable-card" />
      <Route element={<AuthSequencingPreview />} path="auth-sequencing" />
      <Route element={<DrawerComponentsPreview />} path="drawer-components" />
    </Routes>
  );
};

export default Dashboard;
